import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect, Dispatch } from 'react-redux';
import { pick } from 'lodash';
import { OrderedMap } from 'immutable';
import cx from 'classnames';

import { ActionTypes } from 'groups/constants/GroupConstants';
import { Backbone$Id } from 'types/typescript/Backbone';
import { dispatcher } from 'lib/Dispatcher';
import { getFilesArrayFromFileUploads, getFirstFileId, getFirstFile } from 'lib/fileUploadHelper';
// @ts-ignore
import { getDefaultScheduledTime } from 'lib/SchedulingMessage';
import {
  MODAL_FILE_CONTEXT_KEY,
  fetchFileById,
  removeFile,
  resetFile,
  uploadRequest,
} from 'reducers/fileUploads';
// @ts-ignore
import { putMessageRecipientsForTargets } from 'lib/RoleBasedMessagingUtils';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
// @ts-ignore
import CharacterCount from 'lib/CharacterCount';
// @ts-ignore
import Composer from './Composer';
// @ts-ignore
import ComposerFilePreview from './ComposerFilePreview';
// @ts-ignore
import ComposerLinkPreview from 'chat/components/ComposerLinkPreview';
import CurrentUser from 'auth/CurrentUser';
import errorLogger from 'utils/errorLogger';
import EventTracking from 'lib/EventTracking';
import Glyph from 'components/Glyph';
import Message from 'entities/Message';
// @ts-ignore
import RemainingCharactersCount from './RemainingCharactersCount';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
import TranslateButton from 'components/TranslateButton';
import VisualComposerMessageActions from 'chat/actions/VisualComposerMessageActions';
import VisualComposerPrimaryButton from 'chat/components/VisualComposerPrimaryButton';
import VisualComposerScheduler from 'chat/components/VisualComposerScheduler';

import { Target } from 'entities/Message';
import { UploadRequestData } from 'reducers/fileUploads';
import { ContentSourcesContent_me_composer_contentSourcesContent_items_downloadPreview as DownloadPreview } from 'user/queries/__generated__/ContentSourcesContent';

import './styles/VisualComposer.scss';
import { ComposerContext, UIEventContext } from './SplitComposer/components/ComposerContext';

export function fileIdFor(message: Message) {
  const attachmentId = message.getAttachmentId();
  const file = message.getFile();

  if (attachmentId) {
    // Checks for a fileId passed in through query params
    return attachmentId;
  } else if (file) {
    // Checks if the message already has an attachment (scheduled message)
    return file.id;
  }

  return null;
}

type ComposerLinkPreviewType = {
  title: string;
  description: string;
  url: string;
  image: string;
  image_width: string;
  image_height: string;
  rich_content_type: string;
  rich_content_url: string;
  downloadPreview?: DownloadPreview;
};

type Props = {
  autoFocus?: boolean;
  className?: string | null;
  charLimit: number;
  charLimitWithFiles: number;
  constrainMaxHeight?: boolean;
  districtIsSelected?: boolean;
  initialMessage: Message;
  isEditingScheduledMessage?: boolean;
  onMessageSent: (targets: Target[]) => void;
  targets: Target[];
  onBack: () => void;
  orgUuid?: string;
  orgName?: string;
  onRequestHide?: () => void;
  onTextChange: (text: string) => void;
  onScheduledTimeChange: (date?: Date | null) => void;
  text: string;
  scheduledTime?: Date;
  uuidForNewMessage?: string;
  linkPreviews?: OrderedMap<string, ComposerLinkPreviewType> | null;
  onRemoveLinkPreview?: (preview: ComposerLinkPreviewType) => void;
  isUrgentMessage?: boolean;
} & ReduxStateProps &
  ReduxDispatchProps;

type ReduxStateProps = {
  totalRecipientCount: number;
  fileUploads: OrderedMap<Backbone$Id, UploadRequestData>;
};

type ReduxDispatchProps = {
  fetchFileById: (fileContextKey: string, fileId: Backbone$Id) => void;
  removeFile: (fileContextKey: string, fileId: Backbone$Id) => void;
  resetFile: (fileContextKey: string, fileId: Backbone$Id) => void;
  uploadRequest: (fileContextKey: string, fileId: Backbone$Id, blob: File) => void;
};

type State = {
  schedulerOpened: boolean;
  latestTranslation?: string;
};

export class VisualComposer extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { initialMessage, scheduledTime } = props;

    if (initialMessage) {
      const fileId = fileIdFor(initialMessage);
      if (fileId) {
        this.props.fetchFileById(MODAL_FILE_CONTEXT_KEY, fileId);
      }
    }

    this.state = { schedulerOpened: !!scheduledTime };
  }

  handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = event.currentTarget.value;
    const { onTextChange } = this.props;
    onTextChange(text);
  };

  handleToggleScheduler = () => {
    const { onScheduledTimeChange } = this.props;
    const { schedulerOpened } = this.state;
    const scheduledTime = schedulerOpened ? null : getDefaultScheduledTime();

    this.setState({
      schedulerOpened: !schedulerOpened,
    });

    onScheduledTimeChange(scheduledTime);
  };

  handleDatePickerChanged = (inputDate?: Date | null) => {
    const { scheduledTime, onScheduledTimeChange } = this.props;

    if (scheduledTime && inputDate) {
      const oldDate = new Date(scheduledTime.getTime());
      inputDate.setHours(oldDate.getHours());
      inputDate.setMinutes(oldDate.getMinutes());
      onScheduledTimeChange(inputDate);
    }
  };

  handleTimePickerChanged = (inputDate?: Date | null) => {
    const { scheduledTime, onScheduledTimeChange } = this.props;

    if (scheduledTime && inputDate) {
      const newDate = new Date(scheduledTime.getTime());
      newDate.setHours(inputDate.getHours());
      newDate.setMinutes(inputDate.getMinutes());
      onScheduledTimeChange(newDate);
    }
  };

  createMessageFromState = () => {
    const { fileUploads, targets, text, scheduledTime, uuidForNewMessage } = this.props;
    const currentUser = CurrentUser.getUser();

    return Message.createMessage(
      undefined, // use targets not groupcodes
      text,
      scheduledTime,
      getFilesArrayFromFileUploads(fileUploads),
      currentUser.toJSON(),
      undefined, // sent_at does not exist for outgoing message
      targets,
      uuidForNewMessage
    );
  };

  handleMessageSent = () => {
    const { onTextChange, onMessageSent, targets } = this.props;
    onTextChange('');
    onMessageSent(targets);
  };

  handleMessageSentError = (error: Error) => {
    ToastActions.showAPIError(error);
    const dwmPrefix = this.props.districtIsSelected ? 'DWM - ' : '';
    console.warn(error);
    console.error(`${dwmPrefix}VisualComposer sendMessage failed`);
  };

  putMessageInputFromState(senderType: string, senderUuid: string) {
    const {
      targets,
      text,
      fileUploads,
      uuidForNewMessage,
      linkPreviews,
      isUrgentMessage,
    } = this.props;
    const recipients = putMessageRecipientsForTargets(targets);
    const files = getFilesArrayFromFileUploads(fileUploads);
    const previews = linkPreviews ? linkPreviews.toList().toArray() : [];

    return {
      recipients,
      message: {
        body: text,
        fileUuids: files.map(f => f.id),
        linkPreviews: previews.map(pre =>
          pick(pre, [
            'url',
            'title',
            'description',
            'image',
            'image_width',
            'image_height',
            'rich_content_type',
            'rich_content_url',
          ])
        ) as Pick<
          ComposerLinkPreviewType,
          | 'url'
          | 'title'
          | 'description'
          | 'image'
          | 'image_width'
          | 'image_height'
          | 'rich_content_type'
          | 'rich_content_url'
        >[],
        urgent: isUrgentMessage || false,
      },
      uuid: uuidForNewMessage,
      sender: {
        type: senderType,
        uuid: senderUuid,
      },
    };
  }

  sendMessage = (propagateThrow: boolean = false): Promise<void> => {
    const {
      isEditingScheduledMessage,
      scheduledTime,
      uuidForNewMessage,
      initialMessage,
      districtIsSelected,
      orgUuid,
    } = this.props;

    const promiseHandler = (
      promise: Promise<void>,
      errMsg = 'VisualComposer sendMessage failed'
    ) => {
      return promise.then(this.handleMessageSent, this.handleMessageSentError).catch(err => {
        errorLogger(errMsg)(err);
        return propagateThrow ? Promise.reject(err) : undefined;
      });
    };

    const putMessageInput = this.putMessageInputFromState('user', CurrentUser.getUserUuid());
    if (scheduledTime) {
      if (isEditingScheduledMessage) {
        return VisualComposerMessageActions.updateScheduledMessage({
          uuid: uuidForNewMessage ?? '',
          input: {
            messageInput: putMessageInput,
            // for some reason GQL expects a string, but we've always sent a Date
            sendAt: (scheduledTime as unknown) as string,
          },
        })
          .then(() => {
            // Remove the original message in our store because send-to-few creates a new message
            dispatcher.dispatch({
              type: ActionTypes.DESTROY_GROUP_SCHEDULED_MESSAGE,
              message: initialMessage,
            });
            this.handleMessageSent();
          }, this.handleMessageSentError)
          .catch(err => {
            errorLogger('VisualComposer sendMessage failed')(err);
            return propagateThrow ? Promise.reject(err) : undefined;
          });
      } else {
        const schedMessagePromise = VisualComposerMessageActions.scheduleMessage({
          input: {
            messageInput: putMessageInput,
            // for some reason GQL expects a string, but we've always sent a Date
            sendAt: (scheduledTime as unknown) as string,
          },
        });
        return promiseHandler(schedMessagePromise);
      }
    } else if (districtIsSelected) {
      if (!orgUuid) {
        // just making sure this never causes unhandled promises.
        return propagateThrow ? Promise.reject() : Promise.resolve();
      }
      const districtPutMessageInput = this.putMessageInputFromState('district', orgUuid);
      const putDWMMessagePromise = VisualComposerMessageActions.putMessage({
        input: districtPutMessageInput,
      });
      return promiseHandler(
        putDWMMessagePromise,
        'DWM - VisualComposer sendMessage promise failed'
      );
    } else {
      const putMessagePromise = VisualComposerMessageActions.putMessage({ input: putMessageInput });
      return promiseHandler(putMessagePromise);
    }
  };

  isValidMessage = () => {
    const { fileUploads, targets, text } = this.props;

    const isAddressed = targets && targets.length > 0;

    const allUploadsComplete = fileUploads.every(file => {
      return file?.progressPercentage === 100;
    });

    const hasText = text.trim().length > 0;

    return isAddressed && allUploadsComplete && (hasText || !fileUploads.isEmpty());
  };

  isSubmitDisabled = (charactersRemaining: number) => {
    return charactersRemaining < 0 || !this.isValidMessage();
  };

  retryUpload(fileId: Backbone$Id) {
    const { fileUploads } = this.props;
    this.props.resetFile(MODAL_FILE_CONTEXT_KEY, fileId);
    const file = fileUploads.get(fileId);
    if (!file) {
      console.error('VisualComposer - retryUpload missing file');
      return;
    }
    this.props.uploadRequest(MODAL_FILE_CONTEXT_KEY, fileId, file.blob);
  }

  renderFilePreview() {
    const { fileUploads } = this.props;

    const firstFileId = getFirstFileId(fileUploads);
    const uploadRequestData = getFirstFile(fileUploads);
    const removeFirstFile = () =>
      firstFileId && this.props.removeFile(MODAL_FILE_CONTEXT_KEY, firstFileId);
    const retryFirstFile = () => firstFileId && this.retryUpload(firstFileId);

    return (
      uploadRequestData && (
        <div className="VisualComposer-filePreview">
          <ComposerFilePreview
            progress={uploadRequestData.progressPercentage}
            thumbnail={uploadRequestData.thumbnail}
            type={uploadRequestData.file && uploadRequestData.file.getContentType()}
            error={uploadRequestData.isError}
            onDelete={removeFirstFile}
            onRetry={retryFirstFile}
            file={uploadRequestData.file}
          />
        </div>
      )
    );
  }

  renderFooterPanel(charactersRemaining: number, uiEventContext: UIEventContext) {
    const { text, onTextChange } = this.props;
    return (
      <div className="VisualComposer-footerPanel">
        <TranslateButton
          onTextChange={onTextChange}
          onLangChange={lang => {
            this.setState({
              latestTranslation: lang,
            });
          }}
          eventKey="announcements.announcement_modal.universal_composer.show_translation_box.click"
          eventProperties={uiEventContext}
          dataTestSelector="visual_composer_translation_button"
          textToTranslate={text}
        />
        <div className="VisualComposer-remainingCharacterCount">
          <RemainingCharactersCount count={charactersRemaining} />
        </div>
      </div>
    );
  }

  renderDropzone() {
    return (
      <div className="VisualComposer-dropzone">
        <Glyph
          className="VisualComposer-dropzone-icon"
          color="eclipse"
          name="drag-and-drop"
          size="xlarge"
        />
        <Text className="VisualComposer-dropzone-text" color="eclipse" size="xxxsmall">
          Drag and drop here
        </Text>
      </div>
    );
  }

  renderLinkPreviews(linkPreviews: OrderedMap<string, ComposerLinkPreviewType>) {
    const { onRemoveLinkPreview } = this.props;
    const items = linkPreviews.valueSeq().map(preview => {
      if (!preview) {
        return;
      }

      return (
        <ComposerLinkPreview
          key={preview.url}
          linkPreview={preview}
          onRemove={() => {
            if (onRemoveLinkPreview) {
              onRemoveLinkPreview(preview);
            }
          }}
        />
      );
    });
    return <div className="VisualComposer-contentPreviewItems">{items}</div>;
  }

  render() {
    const {
      charLimit,
      charLimitWithFiles,
      constrainMaxHeight,
      autoFocus,
      fileUploads,
      text,
      linkPreviews,
      districtIsSelected,
      isUrgentMessage,
      orgUuid,
      onRequestHide,
      totalRecipientCount,
      targets,
      orgName,
    } = this.props;
    const { schedulerOpened, latestTranslation } = this.state;

    const charactersRemaining = CharacterCount.getRemainingCharactersForText(
      charLimit,
      charLimitWithFiles,
      text.trim(),
      !fileUploads.isEmpty()
    );

    const linkedContentSection =
      linkPreviews && linkPreviews.size > 0 ? this.renderLinkPreviews(linkPreviews) : null;

    const fileSection =
      fileUploads.isEmpty() && !linkedContentSection
        ? this.renderDropzone()
        : this.renderFilePreview();

    return (
      <ComposerContext.Consumer>
        {composerContext => (
          <div
            className={cx('VisualComposer', this.props.className)}
            data-test-selector="visual_composer_textarea">
            <div className="VisualComposer-composerWrapper">
              <div className="VisualComposer-wrap">
                <div className="VisualComposer-composerContainer">
                  <Composer
                    className="VisualComposer-growingComposer"
                    placeholder={'Type your message'}
                    autogrow
                    constrainMaxHeight={constrainMaxHeight}
                    value={text}
                    autoFocus={autoFocus}
                    error={charactersRemaining < 0}
                    onChange={this.handleTextChange}>
                    {this.renderFooterPanel(
                      charactersRemaining,
                      composerContext.getUIEventContext()
                    )}
                    {fileSection}
                    {linkedContentSection}
                  </Composer>
                </div>
                <div className="VisualComposer-buttonContainer">
                  {schedulerOpened ? (
                    <span />
                  ) : (
                    <Button
                      level="secondary"
                      weight="normal"
                      onClick={() => {
                        EventTracking.trackKey(
                          'announcements.share_to.back.click',
                          composerContext.getUIEventContext()
                        );
                        this.props.onBack();
                      }}>
                      Back
                    </Button>
                  )}
                  <Buttons>
                    <VisualComposerScheduler
                      scheduledTime={this.props.scheduledTime}
                      schedulerOpened={schedulerOpened}
                      targets={targets}
                      districtIsSelected={districtIsSelected}
                      onToggleScheduler={this.handleToggleScheduler}
                      onDatePickerChanged={this.handleDatePickerChanged}
                      onTimePickerChanged={this.handleTimePickerChanged}
                      isEditingScheduledMessage={this.props.isEditingScheduledMessage}
                    />
                    <VisualComposerPrimaryButton
                      charactersRemaining={charactersRemaining}
                      districtIsSelected={districtIsSelected}
                      isScheduling={schedulerOpened}
                      isUrgentMessage={isUrgentMessage}
                      latestTranslation={latestTranslation}
                      messageIsValid={this.isValidMessage()}
                      uiEventContext={composerContext.getUIEventContext()}
                      onContinueConfirm={() => {
                        this.sendMessage(true)
                          .then(() => {
                            EventTracking.trackKey(
                              'announcements.composer.confirm_send.send.click',
                              {
                                ...composerContext.getUIEventContext(),
                                organization_uuid: orgUuid,
                              }
                            );
                          })
                          .catch(() => {
                            console.error(
                              `${
                                isUrgentMessage ? 'URGENT' : 'DWM'
                              } - missing district uuid - could not write district message`
                            );
                          });
                        if (onRequestHide) {
                          onRequestHide();
                        }
                      }}
                      onSchedule={() => this.sendMessage()}
                      onSend={() => this.sendMessage()}
                      orgName={orgName}
                      orgUuid={orgUuid}
                      targets={targets}
                      totalRecipientCount={totalRecipientCount}
                    />
                  </Buttons>
                </div>
              </div>
            </div>
          </div>
        )}
      </ComposerContext.Consumer>
    );
  }
}

const mapStateToProps = (state: any): ReduxStateProps => {
  const { fileUploads } = state;
  const fileGroup = fileUploads.get(MODAL_FILE_CONTEXT_KEY);
  return {
    fileUploads: fileGroup || OrderedMap<Backbone$Id, UploadRequestData>(),
    totalRecipientCount: state.roleBasedMessagingSearch.totalRecipientCount,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>): ReduxDispatchProps =>
  bindActionCreators(
    {
      fetchFileById,
      removeFile,
      resetFile,
      uploadRequest,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(VisualComposer);
